<template>
    <nav :class="['sidebar', collapsed ? 'collapsed' : '']">
        <div class="nav-header">
            <template v-if="!isDashboard">
                <a href="#/dashboard" class="link-to-home">
                    <img class="no-print" src="static/img/logo.svg" alt="ascom" width="92" height="37" />
                </a>
            </template>
            <template v-else>
                <img class="no-print" src="static/img/logo.svg" alt="ascom" width="92" height="37" />
            </template>
            <span class="print-only"><i class="icon-logo"></i> &copy; MARi</span>
            <h4 class="print-only">{{ time }}</h4>
        </div>

        <NavigationMenu
            :menu="menu"
            :checkin="checkIn"
            :logout="confirmLogout"
            :disabled="!linksEnabled"
            :collapsed="collapsed"
            :externals-config="externalsConfig"
            :duty-selector="dutySelector"
        />

        <div class="center">
            <h2 class="navbar-username" :title="userName">{{ userName }}</h2>
            <h3>{{ type }}</h3>
            <hr class="no-print" />
            <h2 v-if="organizationName" class="print-only">
                <span>{{ $t('print.organizationName', { val: organizationName }) }}</span>
            </h2>
            <h2 class="print-only">
                <span>{{ $t('print.siteName', { val: user.site_name }) }}</span>
            </h2>
            <h2 v-show="contactInfo" class="print-only contact-info">
                <span>{{ $t('print.contactInformation', { val: contactInfo }) }}</span>
            </h2>
            <h2
                v-if="user.type !== USER_TYPES.SYS_ADMIN && user.type !== USER_TYPES.SUPER_USER"
                class="no-print"
                :title="user.area"
            >
                <span class="print-only-inline">{{ $t('print.assignmentArea') }}&nbsp;</span>{{ user.area }}
            </h2>
            <h2 v-if="user.type !== USER_TYPES.SYS_ADMIN" class="print-only" :title="user.area">
                <span class="print-only-inline">{{ $t('print.assignmentArea') }}&nbsp;</span>{{ user.area }}
            </h2>
            <div
                v-if="user.type === USER_TYPES.SUPER_USER"
                class="aarea-selectbox"
                :class="collapsed ? 'collapsed' : ''"
            >
                <div class="wrapper">
                    <SelectBox
                        ref="area"
                        v-model="current_area"
                        :is-disabled="isLoading"
                        :value="undefined"
                        :options="assignmentAreas(superuserAreas)"
                        @change="current_area"
                        @input="setAarea($event)"
                    />
                </div>
            </div>
            <h3 v-if="organizationName" class="no-print" :title="organizationName">
                {{ organizationName }}
            </h3>
            <button
                class="collapse-btn with-tooltip no-print"
                :data-tooltip="collapsed ? $t('common.expand') : $t('common.collapse')"
                @click="setNavCollapsed(!collapsed)"
            >
                <i class="icon-hide"></i>
            </button>
        </div>
    </nav>
</template>

<script type="text/babel">
import mappingState from './mappingState';
import SelectBox from '../common/SelectBox.vue';
import { mapState, mapMutations, mapActions } from 'vuex';
import { userMenu } from '../../router';
import format from 'date-fns/format';
import NavigationMenu from './NavigationMenu.vue';
import { USER_TYPES, ROUTE_NAMES } from '../../constants';
import { resourceSuperuserAssignmentareas } from '../../api/resources';
import { ifIsNotCanceled, getFullDateFormatNoSeconds } from './helpers';
export default {
    components: { NavigationMenu, SelectBox },

    mixins: [mappingState],

    data() {
        return {
            time: '',
            intervalId: null,
            isDashboard: false,
            current_area: '',
            USER_TYPES,
            linksEnabled: true,
        };
    },

    computed: {
        ...mapState({
            collapsed: state => state.navCollapsed,
            user: state => state.user.user,
            organizationName: state => state.navigation.organizationName,
            contactInfo: state => state.navigation.contactInfo,
            reportType: state => state.selectedReportType,
            checkIn: state => state.checkin,
            unsavedChanges: state => state.unsavedChanges,
            externalsConfig: state => state.externals.configuration,
            superuserAreas: state => state.superUserAreas,
            isLoading: state => state.isLoading,
            dutySelector: state => state.dutySelector.overriden,
        }),

        type() {
            switch (this.user.type) {
                case USER_TYPES.DEVELOPER:
                    return 'Developer';
                case USER_TYPES.SYS_ADMIN:
                    return 'System Administrator';
                case USER_TYPES.USER_ADMIN:
                    return 'User Administrator';
                case USER_TYPES.USER:
                    return 'User';
                case USER_TYPES.SUPER_USER:
                    return 'Super User';
                default:
                    return 'Unknown (type:' + this.user.type + ')';
            }
        },

        userName() {
            return (this.user.first_name + ' ' + this.user.last_name).trim();
        },

        menu() {
            return this.userMenu(this.user);
        },
    },

    watch: {
        $route() {
            this.isDashboard = this.$router.currentRoute.name === 'dashboard';
        },
    },

    created() {
        this.setDateTime();
        this.intervalId = setInterval(this.setDateTime, 10000);
        document.addEventListener('setTimeFormatEvent', this.setDateTime);
    },

    beforeDestroy() {
        clearInterval(this.intervalId);
        document.removeEventListener('setTimeFormatEvent', this.setDateTime);
    },

    methods: {
        userMenu,

        ...mapMutations({
            setUnsavedChanges: 'SET_UNSAVED_CHANGES',
            setLoaded: 'SET_LOADED',
            setExpandedWidgetId: 'SET_WIDGET_ID',
        }),

        ...mapActions({
            logout: 'user/logout',
            setHelpPage: 'navigation/setHelpPage',
            confirmation: 'confirm/confirmation',
            loadNotCheckedInInfo: 'checkin/loadNotCheckedInInfo',
            clearMessages: 'popup/clearMessages',
            loadSuperUserAreas: 'loadSuperuserAreas',
            loadDutySelectorState: 'loadDutySelectorState',
        }),

        setDateTime() {
            this.time = format(new Date(), getFullDateFormatNoSeconds());
        },

        assignmentAreas(list) {
            this.current_area = this.user.area;
            list.forEach(elem => {
                elem.value = elem.name;
            });
            return list;
        },

        setAarea(area) {
            if (area !== this.user.area) {
                this.clearMessages();
                if (this.unsavedChanges) {
                    return this.confirmation({
                        confirmTitle: this.$t('common.unsavedTitle'),
                        confirmBody: this.$t('common.unsavedBody'),
                    }).then(r => {
                        if (r) {
                            this.setUnsavedChanges(false);
                            this.changeAssignmentArea(area);
                        } else {
                            this.$nextTick(() => {
                                this.$refs.area.placeholderText = this.current_area;
                            });
                        }
                    });
                }
                this.changeAssignmentArea(area);
            }
        },

        changeAssignmentArea(area) {
            this.setExpandedWidgetId(null);
            this.showLoading(true);
            this.setLoaded(false);
            this.linksEnabled = false;
            return resourceSuperuserAssignmentareas({
                method: 'post',
                data: { name: area },
            })
                .then(() => {
                    return this.$store
                        .dispatch('loadAssignmentAreas', null, { root: true })
                        .then(() => {
                            this.user.area = this.$store.state.assignmentArea[0].name;
                            this.user.areaId = this.$store.state.assignmentArea[0].id;
                            this.user.areas = this.$store.state.assignmentArea;
                        })
                        .then(() => {
                            if (
                                this.user.visibility.checkin &&
                                this.$router.currentRoute.name !== ROUTE_NAMES.CHECK_IN__NOT_CHECKED_IN
                            ) {
                                this.loadNotCheckedInInfo({ id: this.user.areaId }, { root: true });
                            }
                            this.loadDutySelectorState(this.user.areaId);
                            this.$nextTick(() => {
                                if (this.$router.currentRoute.name !== ROUTE_NAMES.EXTERNAL) {
                                    this.$store.state
                                        .updateData()
                                        .then(() => this.showLoading(false))
                                        .then(() => this.$nextTick(() => this.setLoaded(true)));
                                } else {
                                    this.showLoading(false);
                                    this.setLoaded(true);
                                }
                            });
                        });
                })
                .catch(
                    ifIsNotCanceled({
                        callback: () => {
                            this.loadSuperUserAreas();
                            this.setLoaded(true);
                        },
                    })
                )
                .then(() => {
                    this.linksEnabled = true;
                });
        },

        /**
         * Check app on unsaved changes. If it doesn't have changes performs logout action. If not - confirm dialog
         * will be raised.
         */
        confirmLogout() {
            if (this.unsavedChanges) {
                let confirmTitle = this.isLoading
                    ? this.$t('common.changesLostTitle')
                    : this.$t('common.unsavedTitle');

                return this.confirmation({
                    confirmTitle,
                    confirmBody: this.$t('common.unsavedBody'),
                }).then(r => {
                    if (r) {
                        this.setUnsavedChanges(false);
                        this.logout();
                    }
                });
            }
            this.logout();
        },
    },
};
</script>
